import React, { useEffect, useRef, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import BlockIcon from '@mui/icons-material/Block';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { HangoutParticipant } from '../types';
import { Avatar, InviteStatusBadge } from './common';

export interface ParticipantActions {
  approve: (p: HangoutParticipant) => void;
  decline: (p: HangoutParticipant) => void;
  remove: (p: HangoutParticipant) => void;
  block: (p: HangoutParticipant) => void;
  report: (p: HangoutParticipant) => void;
}

// One person in a hangout: avatar, display name, status. Hosts get approve/decline for join
// requests and remove; everyone can block or report someone else. No contact details are shown.
export function InviteParticipantRow({ participant: p, isHost, busy, actions }: {
  participant: HangoutParticipant; isHost: boolean; busy: boolean; actions: ParticipantActions;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const run = (fn: (p: HangoutParticipant) => void) => () => { setOpen(false); fn(p); };

  return (
    <div className="hg-person">
      <Avatar initials={p.initials} />
      <div style={{ minWidth: 0 }}>
        <b>{p.displayName}{p.isMe ? ' (you)' : ''}</b>
        {p.isHost ? <span className="hg-badge host">Host</span> : <InviteStatusBadge status={p.status} />}
      </div>
      <div className="hg-person-actions" ref={ref}>
        {isHost && p.status === 'Requested' && (
          <>
            <button type="button" className="hg-btn hg-gold hg-sm" disabled={busy} onClick={() => actions.approve(p)} aria-label={`Approve ${p.displayName}`}><CheckIcon fontSize="small" />Approve</button>
            <button type="button" className="hg-btn hg-outline hg-sm" disabled={busy} onClick={() => actions.decline(p)} aria-label={`Decline ${p.displayName}`}><CloseIcon fontSize="small" /></button>
          </>
        )}
        {!p.isMe && (
          <button type="button" className="hg-icon-btn" disabled={busy} aria-label={`More options for ${p.displayName}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}><MoreVertIcon /></button>
        )}
        {open && (
          <div className="hg-menu" role="menu">
            {isHost && !p.isHost && <button role="menuitem" onClick={run(actions.remove)}><PersonRemoveOutlinedIcon fontSize="small" />Remove from hangout</button>}
            <button role="menuitem" className="danger" onClick={run(actions.block)}><BlockIcon fontSize="small" />Block</button>
            <button role="menuitem" onClick={run(actions.report)}><FlagOutlinedIcon fontSize="small" />Report</button>
          </div>
        )}
      </div>
    </div>
  );
}
